"use client";

import React, { useState, useEffect } from "react";
import { ChevronDown, Globe } from "lucide-react";

const languages = [
  { code: "en", name: "EN", flag: "https://flagcdn.com/w20/gb.png" },
  { code: "gu", name: "GU", flag: "https://flagcdn.com/w20/in.png" },
  { code: "hi", name: "HI", flag: "https://flagcdn.com/w20/in.png" },
  { code: "ta", name: "TA", flag: "https://flagcdn.com/w20/in.png" },
];

export const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState("English");

  useEffect(() => {
    // Detect current language from cookie
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(";").shift();
    };

    const cookie = getCookie("googtrans");
    if (cookie) {
      const langCode = cookie.split("/").pop();
      const match = languages.find((l) => l.code === langCode);
      if (match) setCurrentLang(match.name);
    }
  }, []);

  const changeLanguage = (langCode: string, langName: string) => {
    // 1. Clear old cookies and set the new Google Translate cookie
    const domains = [
      window.location.hostname,
      "." + window.location.hostname,
      "",
    ];
    domains.forEach((domain) => {
      document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;${domain ? ` domain=${domain};` : ""}`;
    });

    document.cookie = `googtrans=/en/${langCode}; path=/;`;
    if (window.location.hostname !== "localhost") {
      document.cookie = `googtrans=/en/${langCode}; path=/; domain=.${window.location.hostname};`;
    }

    // 2. Try the DOM trigger as well for instant effect
    const select = document.querySelector(
      ".goog-te-combo",
    ) as HTMLSelectElement;

    if (select) {
      select.value = langCode;
      select.dispatchEvent(new Event("change"));
      select.click();
      setCurrentLang(langName);

      // If it's English, we might need a reload to clear the translation
      if (langCode === "en") {
        setTimeout(() => window.location.reload(), 100);
      }
    } else {
      // 3. Fallback: reload the page to pick up the cookie
      window.location.reload();
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && !(event.target as Element).closest(".language-selector")) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative language-selector notranslate">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-gold-dim/30 hover:border-gold-dim transition-all text-sm text-text-2 cursor-pointer"
      >
        <Globe size={14} className="text-gold-dim" />
        <span className="hidden sm:inline">{currentLang}</span>
        <ChevronDown
          size={14}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-40 bg-black border border-gold-dim shadow-xl rounded-lg overflow-hidden z-50 animate-in fade-in slide-in-from-top-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code, lang.name)}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm text-text-2 hover:bg-gold-dim/10 hover:text-gold-core transition-colors border-b border-white/5 last:border-0 cursor-pointer"
            >
              <img
                src={lang.flag}
                alt={lang.name}
                className="w-5 h-3.5 object-cover rounded-sm"
              />
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
